import cn from 'classnames';
import { compact, find } from 'lodash';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { theme } from 'antd';
import { useTranslation } from 'react-i18next';

import EPTable from '@/comp/EPTable';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { useStores } from '@/hooks/use-stores';
import { maxLength, onlyDigits } from '@/utils/util-functions';
import { isNil, upperCase } from 'lodash/fp';
import TableHeaderCell from '../Common/TableHeaderCell/TableHeaderCell';
import ValidationTooltip from '../Common/ValidationTooltip/ValidationTooltip';
import { CompressorNameContainer, DefaultRuleContainer, Input } from './styled';
import { ENGINE_VERSION_1_0, EngineConfigStatus } from '@/store/Engine/constants';
import { useMeasurement } from '@/hooks/useMeasurement';
import { CustomThemeToken } from '@/theme';

export type DefaultSetpointsFrom = 'rulesEditor' | 'rulesGeneratorModal';

export const LONG_DASH = '\u2014';

const { useToken } = theme;

const CompressorParametersComponent = observer(
  ({
    isEditMode,
    from,
    className,
    disabledCompressors,
    hasTriedToSubmit,
  }: {
    isEditMode: boolean;
    className?: string;
    disabledCompressors?: string;
    hasTriedToSubmit?: boolean;
    from?: DefaultSetpointsFrom;
  }) => {
    const { engineStore } = useStores();
    const { tunit } = useMeasurement();
    const { t } = useTranslation();
    const fromRulesEditor = from === 'rulesEditor';
    const [editingCompressorParameterId, setEditingCompressorParameterId] = useState('');
    const { token } = useToken() as unknown as { token: CustomThemeToken };

    const compressors = useMemo(() => engineStore.applicableCompressorsForEngine, [engineStore.applicableCompressorsForEngine]);
    const compressorParameters = useMemo(() => {
      if (!engineStore.compressorsConfiguration?.compressorParameters || engineStore.isReadOnly) {
        return engineStore.compressorsConfiguration?.compressorParameters;
      }
      const filteredCompressorIds = new Set(compressors.map(c => c.id));
      return engineStore.compressorsConfiguration?.compressorParameters.filter(param => filteredCompressorIds.has(param.deviceId));
    }, [engineStore.compressorsConfiguration?.compressorParameters, compressors, engineStore.isReadOnly]);
    const unit = engineStore.unitMesurementSystem;

    const getPopupContainer = trigger => trigger.parentElement;
    const { tvalue } = useMeasurement();

    useEffect(() => {
      if (hasTriedToSubmit) {
        setEditingCompressorParameterId(null);
      }
    }, [hasTriedToSubmit]);

    useEffect(() => {
      if (engineStore.engineVersion !== ENGINE_VERSION_1_0) {
        engineStore.validateAll();
      }
    }, [engineStore.hasFinishedWizard]);

    const onCompressorParameterFieldChange = (key, field) => e => {
      const newValue = e.target.value;
      setEditingCompressorParameterId(`${key}-${field}`);
      engineStore.compressorsConfiguration.setCompressorParameterField(key, field, newValue);
      engineStore.validateAll(tvalue);
    };

    const onDefaultCompressorParameterKeyPress = event => onlyDigits(event);

    const onInputScroll = e => e?.target?.blur();

    const onInputBlur = () => setEditingCompressorParameterId(null);

    const compressorTypesAbbreviations = {
      [DeviceType.CentrifugalAndPowerSensor]: 'CPS',
    };

    const columns = [
      {
        dataIndex: 'compressorNumber',
        key: 'compressorNumber',
        title: <TableHeaderCell titleKey="No" />,
        width: 100,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => a.compressorNumber - b.compressorNumber,
        render: (compressorNumber, { deviceId }) => {
          return (
            <span className={cn('label-text', disabledCompressors?.includes(deviceId) && 'disabled-compressor')}>
              {compressorNumber}
            </span>
          );
        },
        align: 'center',
      },
      {
        dataIndex: 'deviceId',
        key: 'deviceId',
        title: <TableHeaderCell titleKey="Compressor" className="compressor-header" />,
        width: '30%',
        render: (deviceId, { compressorName }) => {
          const compressor = find(compressors, { id: deviceId });

          return (
            <CompressorNameContainer $disabledCompressor={disabledCompressors?.includes(deviceId)}>
              {compact([
                compressor?.name,
                compressor?.deviceModel?.modelSpecs?.hp,
                compressorTypesAbbreviations[compressor?.deviceModel?.deviceTypes?.[0]],
              ]).join(' | ') || compressorName}
            </CompressorNameContainer>
          );
        },
      },
      {
        dataIndex: 'priority',
        key: 'priority',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('priority')}`} />,

        render: (priority: number, { key }: { key: string }) =>
          !isEditMode && isNil(priority) ? (
            <span className={cn(isEditMode && 'margin-left-2', fromRulesEditor && 'view-mode-table-cell')}>
              {priority ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                (editingCompressorParameterId ? editingCompressorParameterId === `${key}-priority` : true) &&
                find(engineStore.validationMessages, { key, field: 'priority' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'priority' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={priority}
                placeholder="+"
                onChange={e => onCompressorParameterFieldChange(key, 'priority')(e)}
                onWheel={onInputScroll}
                bordered={false}
                count={maxLength(4)}
                onKeyPress={onDefaultCompressorParameterKeyPress}
                onBlur={onInputBlur}
                $valueLength={`${priority ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
                style={{
                  color:
                    engineStore.configurationStatus === EngineConfigStatus.Published
                      ? token.colorBorder
                      : token.colorPrimary,
                  textDecoration:
                    engineStore.configurationStatus === EngineConfigStatus.Published ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'maxTarget',
        key: 'maxTarget',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('maxTarget')} (${tunit('Bar', upperCase(unit))})`} />,

        render: (maxTarget: number, { key }: { key: string }) =>
          !isEditMode && isNil(maxTarget) ? (
            <span className={cn(isEditMode && 'margin-left-2', fromRulesEditor && 'view-mode-table-cell')}>
              {maxTarget ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                (editingCompressorParameterId ? editingCompressorParameterId === `${key}-maxTarget` : true) &&
                find(engineStore.validationMessages, { key, field: 'maxTarget' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'maxTarget' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={maxTarget}
                placeholder="+"
                onChange={onCompressorParameterFieldChange(key, 'maxTarget')}
                onWheel={onInputScroll}
                bordered={false}
                count={maxLength(4)}
                onKeyPress={onDefaultCompressorParameterKeyPress}
                onBlur={onInputBlur}
                $valueLength={`${maxTarget ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
                style={{
                  color:
                    engineStore.configurationStatus === EngineConfigStatus.Published
                      ? token.colorBorder
                      : token.colorPrimary,
                  textDecoration:
                    engineStore.configurationStatus === EngineConfigStatus.Published ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },
      {
        dataIndex: 'minTarget',
        key: 'minTarget',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('minTarget')} (${tunit('Bar', upperCase(unit))})`} />,

        render: (minTarget: number, { key }: { key: string }) =>
          !isEditMode && isNil(minTarget) ? (
            <span className={cn(isEditMode && 'margin-left-2', fromRulesEditor && 'view-mode-table-cell')}>
              {minTarget ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                (editingCompressorParameterId ? editingCompressorParameterId === `${key}-minTarget` : true) &&
                find(engineStore.validationMessages, { key, field: 'minTarget' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'minTarget' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={minTarget}
                placeholder="+"
                onChange={onCompressorParameterFieldChange(key, 'minTarget')}
                onWheel={onInputScroll}
                bordered={false}
                count={maxLength(4)}
                onKeyPress={onDefaultCompressorParameterKeyPress}
                onBlur={onInputBlur}
                $valueLength={`${minTarget ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
                style={{
                  color:
                    engineStore.configurationStatus === EngineConfigStatus.Published
                      ? token.colorBorder
                      : token.colorPrimary,
                  textDecoration:
                    engineStore.configurationStatus === EngineConfigStatus.Published ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },

      {
        dataIndex: 'stopDelayMinutes',
        key: 'stopDelayMinutes',
        width: 150,
        title: <TableHeaderCell titleKey={`${t('timeBetweenUnoadingStop')}`} />,

        render: (stopDelayMinutes: number, { key }: { key: string }) =>
          !isEditMode && isNil(stopDelayMinutes) ? (
            <span className={cn(isEditMode && 'margin-left-2', fromRulesEditor && 'view-mode-table-cell')}>
              {stopDelayMinutes ?? <span>&mdash;</span>}
            </span>
          ) : (
            <ValidationTooltip
              message={
                (editingCompressorParameterId ? editingCompressorParameterId === `${key}-stopDelayMinutes` : true) &&
                find(engineStore.validationMessages, { key, field: 'stopDelayMinutes' })?.message
              }
              tArgs={find(engineStore.validationMessages, { key, field: 'stopDelayMinutes' })?.tArgs}
              placement="topRight"
              getPopupContainer={getPopupContainer}
              overlayInnerStyle={{ minWidth: 200 }}
            >
              <Input
                value={stopDelayMinutes}
                placeholder="+"
                onChange={onCompressorParameterFieldChange(key, 'stopDelayMinutes')}
                onWheel={onInputScroll}
                bordered={false}
                count={maxLength(4)}
                onKeyPress={onDefaultCompressorParameterKeyPress}
                onBlur={onInputBlur}
                $valueLength={`${stopDelayMinutes ?? ''}`?.length}
                $hasFinishedWizard={engineStore.hasFinishedWizard}
                style={{
                  color:
                    engineStore.configurationStatus === EngineConfigStatus.Published
                      ? token.colorBorder
                      : token.colorPrimary,
                  textDecoration:
                    engineStore.configurationStatus === EngineConfigStatus.Published ? 'none' : 'underline',
                }}
              />
            </ValidationTooltip>
          ),
      },
    ];
    return compressorParameters?.length ? (
      <DefaultRuleContainer
        className={className}
        $fromRulesEditor={fromRulesEditor}
        $hasFinishedWizard={engineStore.hasFinishedWizard}
        $isReadOnly={engineStore.isReadOnly}
      >
        <EPTable
          columns={columns}
          dataSource={compressorParameters}
          pagination={false}
          className="table"
          scroll={{ x: columns.length * 100 }}
        />
      </DefaultRuleContainer>
    ) : null;
  },
);

export default CompressorParametersComponent;
